import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TablePagination, Button, TextField, CircularProgress,
  Chip, Snackbar, Alert, Card, CardContent, Tabs, Tab, MenuItem, Select, FormControl, InputLabel, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import HelpIcon from '@mui/icons-material/Help';
import DownloadIcon from '@mui/icons-material/Download';

const COLORS = ['#8884d8', '#82ca9d', '#ff7300', '#ffbb28', '#ff8042', '#00C49F', '#FF5733', '#C70039'];

const formatDate = (date) => date.toISOString().split('T')[0];

const FinancialReport = () => {
  const { adminUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState(null);
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [doctorFilter, setDoctorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('today');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [forecastDialogOpen, setForecastDialogOpen] = useState(false);

  const fetchFinancialData = async () => {
    if (!adminUser) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      setLoading(true);
      const response = await api.get('/admin/financial-report', {
        ...config,
        params: { startDate, endDate, doctorId: doctorFilter, status: statusFilter, state: stateFilter, city: cityFilter },
      });
      console.log('Financial Data:', response.data);
      setFinancialData(response.data);
    } catch (err) {
      console.error('Financial report fetch error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: 'Failed to fetch financial report', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorDetails = async (doctorId) => {
    try {
      const response = await api.get(`/admin/doctor-payment-details/${doctorId}`, {
        headers: { Authorization: `Bearer ${adminUser.accessToken}` },
      });
      setDoctorDetails(response.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch doctor details', severity: 'error' });
    }
  };

  const handleDateRangeChange = (event) => {
    const value = event.target.value;
    setDateRangeFilter(value);
    const today = new Date();
    let newStartDate;

    switch (value) {
      case 'today':
        newStartDate = new Date(today);
        setStartDate(formatDate(newStartDate));
        setEndDate(formatDate(today));
        break;
      case 'last7days':
        newStartDate = new Date(today);
        newStartDate.setDate(today.getDate() - 7);
        setStartDate(formatDate(newStartDate));
        setEndDate(formatDate(today));
        break;
      case 'lastMonth':
        newStartDate = new Date(today);
        newStartDate.setMonth(today.getMonth() - 1);
        setStartDate(formatDate(newStartDate));
        setEndDate(formatDate(today));
        break;
      case 'last6Months':
        newStartDate = new Date(today);
        newStartDate.setMonth(today.getMonth() - 6);
        setStartDate(formatDate(newStartDate));
        setEndDate(formatDate(today));
        break;
      case 'lastYear':
        newStartDate = new Date(today);
        newStartDate.setFullYear(today.getFullYear() - 1);
        setStartDate(formatDate(newStartDate));
        setEndDate(formatDate(today));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (adminUser) fetchFinancialData();
  }, [adminUser, startDate, endDate, doctorFilter, statusFilter, stateFilter, cityFilter]);

  const handleFilter = () => fetchFinancialData();

  const handleRefund = async (appointmentId) => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const refundAmount = financialData?.refundReport?.find(r => r.appointmentId === appointmentId)?.refundAmount || 0;
      await api.post(`/admin/payments/refund/${appointmentId}`, { refundAmount }, config);
      setSnackbar({ open: true, message: 'Refund processed successfully', severity: 'success' });
      fetchFinancialData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to process refund', severity: 'error' });
    }
  };

  const handleBulkPayout = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const withdrawalIds = financialData?.doctorEarnings?.filter(doc => doc.pendingPayout > 0).map(doc => doc.doctorId) || [];
      await api.post('/admin/withdrawals/bulk-approve', { withdrawalIds }, config);
      setSnackbar({ open: true, message: 'Bulk payout processed successfully', severity: 'success' });
      fetchFinancialData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to process bulk payout', severity: 'error' });
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text('TureMedicine Financial Report', 14, 20);
    doc.text(`Date Range: ${startDate} to ${endDate}`, 14, 30);
    doc.autoTable({
      startY: 40,
      head: [['Metric', 'Value']],
      body: [
        ['Total Appointments Booked', financialData?.totalAppointments || 0],
        ['Total Completed Appointments', financialData?.completedAppointments || 0],
        ['Deferred Revenue', `₹${(financialData?.deferredRevenue || 0).toLocaleString()}`],
        ['Gross Revenue', `₹${(financialData?.grossRevenue || 0).toLocaleString()}`],
        ['Refunded Amount', `₹${(financialData?.refundedAmount || 0).toLocaleString()}`],
        ['Doctor Earnings (Before Fees)', `₹${(financialData?.doctorEarningsBeforeFees || 0).toLocaleString()}`],
        ['Platform Commission from Patients', `₹${(financialData?.platformCommissionFromPatients || 0).toLocaleString()}`],
        ['Platform Deduction from Doctors', `₹${(financialData?.platformDeductionFromDoctors || 0).toLocaleString()}`],
        ['Doctor Payouts (After Fees)', `₹${(financialData?.doctorPayouts || 0).toLocaleString()}`],
        ['Net Revenue (Platform Earnings)', `₹${(financialData?.netRevenue || 0).toLocaleString()}`],
        ['Average Revenue Per Appointment (ARPA)', `₹${(financialData?.arpa || 0).toLocaleString()}`],
        ['Total Transactions', financialData?.totalTransactions || 0],
        ['GST Collected', `₹${(financialData?.gstCollected || 0).toLocaleString()}`],
        ['Platform Total Revenue', `₹${(financialData?.platformTotalRevenue || 0).toLocaleString()}`],
        ['Payment Gateway Fees', `₹${(financialData?.paymentGatewayFees || 0).toLocaleString()}`],
        ['GST on Payment Gateway Fees', `₹${(financialData?.gstOnPaymentGatewayFees || 0).toLocaleString()}`],
        ['Rest Amount After Refund', `₹${(financialData?.restAmountAfterRefund || 0).toLocaleString()}`],
        ['Net Platform Profit', `₹${(financialData?.netPlatformProfit || 0).toLocaleString()}`],
      ],
    });
    doc.save('financial_report.pdf');
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet([
      { Metric: 'Total Appointments Booked', Value: financialData?.totalAppointments || 0 },
      { Metric: 'Total Completed Appointments', Value: financialData?.completedAppointments || 0 },
      { Metric: 'Deferred Revenue', Value: `₹${(financialData?.deferredRevenue || 0).toLocaleString()}` },
      { Metric: 'Gross Revenue', Value: `₹${(financialData?.grossRevenue || 0).toLocaleString()}` },
      { Metric: 'Refunded Amount', Value: `₹${(financialData?.refundedAmount || 0).toLocaleString()}` },
      { Metric: 'Doctor Earnings (Before Fees)', Value: `₹${(financialData?.doctorEarningsBeforeFees || 0).toLocaleString()}` },
      { Metric: 'Platform Commission from Patients', Value: `₹${(financialData?.platformCommissionFromPatients || 0).toLocaleString()}` },
      { Metric: 'Platform Deduction from Doctors', Value: `₹${(financialData?.platformDeductionFromDoctors || 0).toLocaleString()}` },
      { Metric: 'Doctor Payouts (After Fees)', Value: `₹${(financialData?.doctorPayouts || 0).toLocaleString()}` },
      { Metric: 'Net Revenue (Platform Earnings)', Value: `₹${(financialData?.netRevenue || 0).toLocaleString()}` },
      { Metric: 'Average Revenue Per Appointment (ARPA)', Value: `₹${(financialData?.arpa || 0).toLocaleString()}` },
      { Metric: 'Total Transactions', Value: financialData?.totalTransactions || 0 },
      { Metric: 'GST Collected', Value: `₹${(financialData?.gstCollected || 0).toLocaleString()}` },
      { Metric: 'Platform Total Revenue', Value: `₹${(financialData?.platformTotalRevenue || 0).toLocaleString()}` },
      { Metric: 'Payment Gateway Fees', Value: `₹${(financialData?.paymentGatewayFees || 0).toLocaleString()}` },
      { Metric: 'GST on Payment Gateway Fees', Value: `₹${(financialData?.gstOnPaymentGatewayFees || 0).toLocaleString()}` },
      { Metric: 'Rest Amount After Refund', Value: `₹${(financialData?.restAmountAfterRefund || 0).toLocaleString()}` },
      { Metric: 'Net Platform Profit', Value: `₹${(financialData?.netPlatformProfit || 0).toLocaleString()}` },
      ...(financialData?.doctorEarnings || []).map(doc => ({
        'Doctor Name': doc?.doctorName || 'N/A',
        'Earnings Before Fees (₹)': (doc?.earningsBeforeFees || 0).toLocaleString(),
        'Platform Deduction (₹)': (doc?.platformDeduction || 0).toLocaleString(),
        'Net Payout (₹)': (doc?.netPayout || 0).toLocaleString(),
        'Pending Payout (₹)': (doc?.pendingPayout || 0).toLocaleString(),
        'Total Appointments Booked': doc?.totalAppointments || 0,
        'Completed Appointments': doc?.completedAppointments || 0,
        'State': doc?.state || 'N/A',
        'City': doc?.city || 'N/A',
      })),
      ...(financialData?.patientTransactions || []).map(tx => ({
        'Patient Name': tx?.patientName || 'N/A',
        'Appointment ID': tx?.appointmentId || 'N/A',
        'Total Paid (₹)': (tx?.totalPaid || 0).toLocaleString(),
        'Amount (₹)': (tx?.amount || 0).toLocaleString(),
        'Status': tx?.status || 'N/A',
        'Date': tx?.date ? new Date(tx.date).toLocaleDateString() : 'N/A',
      })),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financial Report');
    XLSX.writeFile(wb, 'financial_report.xlsx');
  };

  const handleExportCSV = () => {
    const csvData = [
      ['Metric', 'Value'],
      ['Total Appointments Booked', financialData?.totalAppointments || 0],
      ['Total Completed Appointments', financialData?.completedAppointments || 0],
      ['Deferred Revenue', `₹${(financialData?.deferredRevenue || 0).toLocaleString()}`],
      ['Gross Revenue', `₹${(financialData?.grossRevenue || 0).toLocaleString()}`],
      ['Refunded Amount', `₹${(financialData?.refundedAmount || 0).toLocaleString()}`],
      ['Doctor Earnings (Before Fees)', `₹${(financialData?.doctorEarningsBeforeFees || 0).toLocaleString()}`],
      ['Platform Commission from Patients', `₹${(financialData?.platformCommissionFromPatients || 0).toLocaleString()}`],
      ['Platform Deduction from Doctors', `₹${(financialData?.platformDeductionFromDoctors || 0).toLocaleString()}`],
      ['Doctor Payouts (After Fees)', `₹${(financialData?.doctorPayouts || 0).toLocaleString()}`],
      ['Net Revenue (Platform Earnings)', `₹${(financialData?.netRevenue || 0).toLocaleString()}`],
      ['Average Revenue Per Appointment (ARPA)', `₹${(financialData?.arpa || 0).toLocaleString()}`],
      ['Total Transactions', financialData?.totalTransactions || 0],
      ['GST Collected', `₹${(financialData?.gstCollected || 0).toLocaleString()}`],
      ['Platform Total Revenue', `₹${(financialData?.platformTotalRevenue || 0).toLocaleString()}`],
      ['Payment Gateway Fees', `₹${(financialData?.paymentGatewayFees || 0).toLocaleString()}`],
      ['GST on Payment Gateway Fees', `₹${(financialData?.gstOnPaymentGatewayFees || 0).toLocaleString()}`],
      ['Rest Amount After Refund', `₹${(financialData?.restAmountAfterRefund || 0).toLocaleString()}`],
      ['Net Platform Profit', `₹${(financialData?.netPlatformProfit || 0).toLocaleString()}`],
    ];
    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'financial_report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDoctorClick = (doctorId) => {
    setSelectedDoctor(doctorId);
    fetchDoctorDetails(doctorId);
  };

  if (loading) return <Box display="flex" justifyContent="center"><CircularProgress /></Box>;
  if (!financialData) return <Typography>No financial data available</Typography>;

  const {
    deferredRevenue = 0,
    grossRevenue = 0,
    refundedAmount = 0,
    netRevenue = 0,
    doctorEarningsBeforeFees = 0,
    platformCommissionFromPatients = 0,
    platformDeductionFromDoctors = 0,
    doctorPayouts = 0,
    totalAppointments = 0,
    completedAppointments = 0,
    canceledAppointments = 0,
    revenueBreakdownByStatus = {
      pending: { count: 0, amount: 0 },
      accepted: { count: 0, amount: 0 },
      rescheduled: { count: 0, amount: 0 },
      rejected: { count: 0, amount: 0 },
      completed: { count: 0, amount: 0 },
      canceled: { count: 0, amount: 0 },
    },
    doctorEarnings = [],
    refundReport = [],
    monthlyRevenue = [],
    totalPendingPayouts = 0,
    totalPendingRefunds = 0,
    dailyRevenue = [],
    totalTransactions = 0,
    transactionBreakdown = { successful: 0, refunded: 0, pending: 0, failed: 0 },
    arpa = 0,
    patientTransactions = [],
    commissionBreakdown = { patientCommission: 0, doctorDeduction: 0 },
    gstCollected = 0,
    revenueForecast = [],
    platformTotalRevenue = 0,
    paymentGatewayFees = 0,
    gstOnPaymentGatewayFees = 0,
    netPlatformProfit = 0,
    restAmountAfterRefund = 0,
  } = financialData;

  const filteredRefundReport = refundReport.filter(refund =>
    (refund?.patientName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (refund?.appointmentId?.toString() || '').includes(searchQuery)
  );
  const filteredDoctorEarnings = doctorEarnings.filter(doc =>
    (doc?.doctorName?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );
  const filteredPatientTransactions = patientTransactions.filter(tx =>
    (tx?.patientName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (tx?.appointmentId?.toString() || '').includes(searchQuery)
  );

  const uniqueStates = [...new Set(doctorEarnings.map(doc => doc?.state || 'N/A'))].sort();
  const uniqueCities = [...new Set(doctorEarnings.map(doc => doc?.city || 'N/A'))].sort();

  // Chart Data Preparation with safeguards
  const monthlyChartData = (monthlyRevenue || []).map(item => ({
    name: item?.month || 'N/A',
    GrossRevenue: item?.grossRevenue || 0,
    DoctorPayouts: item?.doctorPayouts || 0,
    RefundedAmount: item?.refundedAmount || 0,
    TotalAppointments: item?.totalAppointments || 0,
    CompletedAppointments: item?.completedAppointments || 0,
    CanceledAppointments: item?.canceledAppointments || 0,
  }));

  const dailyRevenueChartData = (dailyRevenue || []).map(item => ({
    date: item?.date || 'N/A',
    GrossRevenue: item?.grossRevenue || 0,
    DeferredRevenue: item?.deferredRevenue || 0,
    NetRevenue: item?.netRevenue || 0,
    Refunds: item?.refundedAmount || 0,
    DoctorPayouts: item?.doctorPayouts || 0,
  }));

  const revenueCategoryPieData = [
    { name: 'Completed', value: revenueBreakdownByStatus.completed?.amount || 0 },
    { name: 'Rescheduled', value: revenueBreakdownByStatus.rescheduled?.amount || 0 },
    { name: 'Canceled', value: revenueBreakdownByStatus.canceled?.amount || 0 },
    { name: 'Refunded', value: refundedAmount || 0 },
  ].filter(item => item.value > 0);

  const doctorVsPlatformBarData = [
    { name: 'Doctors', Earnings: doctorPayouts || 0 },
    { name: 'Platform', Earnings: netRevenue || 0 },
  ];

  const forecastChartData = (revenueForecast || []).map(item => ({
    date: item?.date || 'N/A',
    PredictedRevenue: item?.predictedRevenue || 0,
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Admin Financial Report - TureMedicine</Typography>

      {/* Filters & Search */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select value={dateRangeFilter} onChange={handleDateRangeChange}>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="last7days">Last 7 Days</MenuItem>
            <MenuItem value="lastMonth">Last Month</MenuItem>
            <MenuItem value="last6Months">Last 6 Months</MenuItem>
            <MenuItem value="lastYear">Last 1 Year</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Doctor</InputLabel>
          <Select value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {(doctorEarnings || []).map(doc => (
              <MenuItem key={doc?.doctorId || 'unknown'} value={doc?.doctorId || ''}>
                {doc?.doctorName || 'N/A'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>State</InputLabel>
          <Select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {uniqueStates.map(state => <MenuItem key={state} value={state}>{state}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>City</InputLabel>
          <Select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {uniqueCities.map(city => <MenuItem key={city} value={city}>{city}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Patient, Doctor, or Appointment ID"
        />
        <Button variant="contained" onClick={handleFilter}>Apply Filters</Button>
      </Box>

      {/* Export Buttons */}
      <Box display="flex" gap={2} mb={3}>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportPDF}>Export to PDF</Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportExcel}>Export to Excel</Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV}>Export to CSV</Button>
        <Button variant="outlined" onClick={() => setForecastDialogOpen(true)}>View Revenue Forecast</Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab label="Overview" />
        <Tab label="Revenue Breakdown" />
        <Tab label="Deferred & Pending" />
        <Tab label="Doctor Earnings" />
        <Tab label="Patient Transactions" />
        <Tab label="Graphical Insights" />
        <Tab label="Tax & Compliance" />
        <Tab label="Platform Financial Report" />
      </Tabs>

      {/* 1️⃣ Overview Dashboard */}
      {tabValue === 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Financial Overview</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">📅 Total Appointments Booked</Typography>
                  <Typography variant="h5">{totalAppointments}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">✅ Total Completed Appointments</Typography>
                  <Typography variant="h5">{completedAppointments}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">📉 Deferred Revenue</Typography>
                  <Typography variant="h5">₹{deferredRevenue.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">💰 Gross Revenue</Typography>
                  <Typography variant="h5">₹{grossRevenue.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">❌ Refunded Amount</Typography>
                  <Typography variant="h5">₹{refundedAmount.toLocaleString()}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    (Actual amount refunded to patients)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">👩‍⚕️ Doctor Earnings (Before Fees)</Typography>
                  <Typography variant="h5">₹{doctorEarningsBeforeFees.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">🏦 Net Revenue</Typography>
                  <Typography variant="h5">₹{netRevenue.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">💸 Platform Commission from Patients</Typography>
                  <Typography variant="h5">₹{platformCommissionFromPatients.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">💸 Platform Deduction from Doctors</Typography>
                  <Typography variant="h5">₹{platformDeductionFromDoctors.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">💳 Doctor Payouts</Typography>
                  <Typography variant="h5">₹{doctorPayouts.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">⏳ Pending Doctor Payouts</Typography>
                  <Typography variant="h5">₹{totalPendingPayouts.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">📊 ARPA (Avg Revenue Per Appointment)</Typography>
                  <Typography variant="h5">₹{arpa.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">💳 Total Transactions</Typography>
                  <Typography variant="h5">{totalTransactions}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6">📑 GST Collected</Typography>
                  <Typography variant="h5">₹{gstCollected.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 2️⃣ Revenue Breakdown */}
      {tabValue === 1 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Revenue Breakdown by Appointment Status</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Appointment Status</TableCell>
                <TableCell align="right">Number of Appointments</TableCell>
                <TableCell align="right">Total Amount Collected (₹)</TableCell>
                <TableCell>Revenue Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(revenueBreakdownByStatus).map(([status, data]) => (
                <TableRow key={status}>
                  <TableCell>{status.charAt(0).toUpperCase() + status.slice(1)}</TableCell>
                  <TableCell align="right">{data?.count || 0}</TableCell>
                  <TableCell align="right">{(data?.amount || 0).toLocaleString()}</TableCell>
                  <TableCell>{status === 'completed' ? 'Gross Revenue' : status === 'canceled' ? 'Refunded Amount' : 'Deferred Revenue'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* 3️⃣ Deferred Revenue & Pending Payments */}
      {tabValue === 2 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Deferred Revenue & Pending Payments</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Total Deferred Revenue</TableCell>
                <TableCell align="right">{deferredRevenue.toLocaleString()}</TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pending Payouts to Doctors</TableCell>
                <TableCell align="right">{totalPendingPayouts.toLocaleString()}</TableCell>
                <TableCell>{totalPendingPayouts > 0 && <Button variant="contained" onClick={handleBulkPayout}>Release Payouts</Button>}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pending Refunds</TableCell>
                <TableCell align="right">{totalPendingRefunds.toLocaleString()}</TableCell>
                <TableCell>{totalPendingRefunds > 0 && <Button variant="contained" onClick={() => setTabValue(4)}>Process Refunds</Button>}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* 4️⃣ Doctor Earnings & Payouts */}
      {tabValue === 3 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Doctor Earnings Dashboard</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Doctor Name</TableCell>
                  <TableCell align="right">Earnings Before Fees (₹)</TableCell>
                  <TableCell align="right">Platform Deduction (₹)</TableCell>
                  <TableCell align="right">Net Payout (₹)</TableCell>
                  <TableCell align="right">Pending Payout (₹)</TableCell>
                  <TableCell align="right">Total Appointments</TableCell>
                  <TableCell align="right">Completed Appointments</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDoctorEarnings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((doc) => (
                  <TableRow key={doc?.doctorId || Math.random()}>
                    <TableCell>{doc?.doctorName || 'N/A'}</TableCell>
                    <TableCell align="right">{(doc?.earningsBeforeFees || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(doc?.platformDeduction || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(doc?.netPayout || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(doc?.pendingPayout || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{doc?.totalAppointments || 0}</TableCell>
                    <TableCell align="right">{doc?.completedAppointments || 0}</TableCell>
                    <TableCell>
                      <Button variant="outlined" onClick={() => handleDoctorClick(doc?.doctorId || '')}>
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDoctorEarnings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <Typography variant="body1" mt={2}>🔹 Total Doctor Payouts: ₹{doctorPayouts.toLocaleString()}</Typography>
          <Typography variant="body1">🔹 Total Pending Payouts: ₹{totalPendingPayouts.toLocaleString()}</Typography>
        </Paper>
      )}

      {/* 5️⃣ Patient Transactions & Refund History */}
      {tabValue === 4 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Patient Payment & Refund History</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Appointment ID</TableCell>
                  <TableCell align="right">Total Paid (₹)</TableCell>
                  <TableCell align="right">Amount (₹)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPatientTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((tx) => (
                  <TableRow key={tx?.appointmentId || Math.random()}>
                    <TableCell>{tx?.patientName || 'N/A'}</TableCell>
                    <TableCell>{tx?.appointmentId || 'N/A'}</TableCell>
                    <TableCell align="right">{(tx?.totalPaid || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(tx?.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={tx?.status || 'N/A'}
                        color={tx?.status === 'successful' ? 'success' : tx?.status === 'refunded' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{tx?.date ? new Date(tx.date).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>
                      {tx?.status === 'pending' && (
                        <Button variant="contained" color="primary" onClick={() => handleRefund(tx?.appointmentId || '')}>
                          Process Refund
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPatientTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* 6️⃣ Graphical Insights */}
      {tabValue === 5 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Graphical Insights</Typography>
          
          <Typography variant="subtitle1">Daily Revenue Trend</Typography>
          <LineChart width={Math.min(window.innerWidth - 100, 800)} height={300} data={dailyRevenueChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Line type="monotone" dataKey="GrossRevenue" stroke="#8884d8" name="Gross Revenue" />
            <Line type="monotone" dataKey="DeferredRevenue" stroke="#82ca9d" name="Deferred Revenue" />
            <Line type="monotone" dataKey="NetRevenue" stroke="#ff7300" name="Net Revenue" />
            <Line type="monotone" dataKey="Refunds" stroke="#ffbb28" name="Refunds" />
            <Line type="monotone" dataKey="DoctorPayouts" stroke="#00C49F" name="Doctor Payouts" />
          </LineChart>

          <Typography variant="subtitle1" mt={3}>Revenue Category Breakdown</Typography>
          <PieChart width={400} height={400}>
            <Pie
              data={revenueCategoryPieData}
              cx={200}
              cy={200}
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {revenueCategoryPieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip />
          </PieChart>

          <Typography variant="subtitle1" mt={3}>Doctor Earnings vs Platform Earnings</Typography>
          <BarChart width={Math.min(window.innerWidth - 100, 800)} height={300} data={doctorVsPlatformBarData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Bar dataKey="Earnings" fill="#8884d8" />
          </BarChart>

          <Typography variant="subtitle1" mt={3}>Monthly Revenue & Growth</Typography>
          <BarChart width={Math.min(window.innerWidth - 100, 800)} height={300} data={monthlyChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Bar dataKey="GrossRevenue" fill="#8884d8" name="Gross Revenue" />
            <Bar dataKey="DoctorPayouts" fill="#82ca9d" name="Doctor Payouts" />
            <Bar dataKey="RefundedAmount" fill="#ff7300" name="Refunded Amount" />
          </BarChart>
        </Paper>
      )}

      {/* 7️⃣ Tax & Compliance */}
      {/* 7️⃣ Tax & Compliance */}
      {tabValue === 6 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Tax & Compliance Reports</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Total Taxable Revenue (Net Revenue)</TableCell>
                <TableCell align="right">{(netRevenue || 0).toLocaleString()}</TableCell>
                <TableCell>Platform earnings after doctor payouts</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>GST Collected (18% of Net Revenue)</TableCell>
                <TableCell align="right">{((netRevenue * 0.18) || 0).toLocaleString()}</TableCell>
                <TableCell>GST applicable on platform&apos;s net revenue</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* 8️⃣ Platform Financial Report */}
      {tabValue === 7 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>TureMedicine Platform Financial Report</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Total Platform Revenue (Commissions from Patients + Doctors)</TableCell>
                <TableCell align="right">{platformTotalRevenue.toLocaleString()}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Payment Gateway Fees (2% of Gross Revenue)</TableCell>
                <TableCell align="right">{paymentGatewayFees.toLocaleString()}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>GST on Payment Gateway Fees (18% of Gateway Fees)</TableCell>
                <TableCell align="right">{gstOnPaymentGatewayFees.toLocaleString()}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rest Amount After Refund (Retained after gateway fees and refunds)</TableCell>
                <TableCell align="right">{restAmountAfterRefund.toLocaleString()}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Net Platform Profit (After Gateway Fees & GST + Rest Amount)</TableCell>
                <TableCell align="right">{netPlatformProfit.toLocaleString()}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Doctor Details Dialog */}
      <Dialog open={!!selectedDoctor} onClose={() => setSelectedDoctor(null)} maxWidth="md" fullWidth>
        <DialogTitle>Doctor Earnings Dashboard</DialogTitle>
        <DialogContent>
          {doctorDetails ? (
            <Box>
              <Typography variant="h6">Doctor Information</Typography>
              <Typography>Name: {doctorDetails?.name || 'N/A'}</Typography>
              <Typography>Total Earnings (Before Fees): ₹{(doctorDetails?.summary?.totalEarningsBeforeFees || 0).toLocaleString()}</Typography>
              <Typography>Platform Deduction: ₹{(doctorDetails?.summary?.platformDeductionFromDoctors || 0).toLocaleString()}</Typography>
              <Typography>Net Earnings: ₹{(doctorDetails?.summary?.netDoctorEarnings || 0).toLocaleString()}</Typography>
              <Typography>Total Withdrawn: ₹{(doctorDetails?.summary?.totalWithdrawn || 0).toLocaleString()}</Typography>
              <Typography>Pending Earnings: ₹{(doctorDetails?.summary?.pendingEarnings || 0).toLocaleString()}</Typography>
              <Typography>Completed Appointments: {doctorDetails?.summary?.completedAppointments || 0}</Typography>
            </Box>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDoctor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Revenue Forecast Dialog */}
      <Dialog open={forecastDialogOpen} onClose={() => setForecastDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Revenue Forecast</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">Predicted Revenue (Next 30 Days)</Typography>
          <LineChart width={Math.min(window.innerWidth - 100, 800)} height={300} data={forecastChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Line type="monotone" dataKey="PredictedRevenue" stroke="#8884d8" name="Predicted Revenue" />
          </LineChart>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForecastDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialReport;